/**
 * /client-engagement/searches — the My Searches list.
 *
 * Reads the Postgres mirror of Thrive's searches catalog through
 * `GET /api/web/v2/searches-list`. Defaults to the caller's own Open +
 * Business Development searches; the owner toggle widens to the whole firm
 * (firm-wide visibility with attribution), the status pills narrow, and the
 * text box searches the search name and the client company. Every filter AND
 * the sort are applied SERVER-side — the catalog is ~4,700 rows, so there is no
 * version of this page that loads them all and filters or orders them in the
 * browser (Trello #813: the table used to sort the page it held).
 *
 * No aggregate strip / KPI row anywhere on the page: "focus on one search at a
 * time" is an explicit product guardrail, not an omission.
 *
 * Row click → Search Detail (`:searchId`). Tokenized — design-system token
 * utilities only, no inline `style={{}}`, English copy only.
 */
import type {
  SearchesListResponse,
  SearchesListSortKey,
  SearchStatusFilter,
} from "@nu-assist/contracts";
import { listPageSize, useMe } from "@nu-assist/shared-fe";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { STATUS_PILLS } from "./SearchesToolbar";

import { RetryButton } from "../../../components/RetryButton";
import { TablePagination } from "../../../components/TablePagination";
import { type SearchesClient, createSearchesClient } from "../../../clients/searches.client";
import { SearchesTable, isSortKey } from "./SearchesTable";
import { SearchesToolbar } from "./SearchesToolbar";
import { type SearchesSort, useSearchesList } from "./useSearchesList";
import { useSearchLeads } from "./useSearchLeads";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

const SEARCH_DEBOUNCE_MS = 300;

export interface SearchesPageProps {
  /** Injectable for tests (defaults to the module-shared typed client). */
  client?: SearchesClient;
  /** Injectable caller email for tests; defaults to `useMe()`. */
  meEmailOverride?: string;
}

// One client per app session (lazy-resolves `globalThis.fetch` at call time, so
// MSW interception still applies — see the client's note).
const defaultClient = createSearchesClient({ apiBase: API_BASE });

/** The status filters this page honours in `?status=` — derived from the
 *  toolbar's pills so a fifth filter cannot gain a pill while staying
 *  unaddressable from the URL (or vice versa). */
const STATUS_FILTERS: readonly SearchStatusFilter[] = STATUS_PILLS.map((p) => p.value);

function readStatusParam(params: URLSearchParams): SearchStatusFilter | null {
  const raw = params.get("status");
  return STATUS_FILTERS.includes(raw as SearchStatusFilter) ? (raw as SearchStatusFilter) : null;
}

/** `?sort=&order=` → the sort state, or none. An unknown column is "no sort"
 *  (the API would 422 it, and a typo in a shared link must not empty the
 *  list); an unknown or absent `order` with a known column is `asc`, which is
 *  what the API defaults to when `order` is omitted. */
function readSortParams(params: URLSearchParams): SearchesSort {
  const key = params.get("sort");
  if (!isSortKey(key)) return null;
  return { key, dir: params.get("order") === "desc" ? "desc" : "asc" };
}

export function SearchesPage({ client, meEmailOverride }: SearchesPageProps = {}) {
  // Rows per page, resolved per stage from `@nu-assist/shared-fe` (local 20 /
  // dev 50 / prod 100) so this list moves in lockstep with People Lists and the
  // contacts table. The same value feeds the query and the pager below, so the
  // two cannot drift from each other.
  const pageSize = listPageSize();
  const activeClient = client ?? defaultClient;
  const [searchParams, setSearchParams] = useSearchParams();

  // The AgendaGPT column's output channel: the n8n workflow mails the agenda to
  // this address. Resolved HERE rather than in `SearchesTable` so the table
  // stays presentational — it is rendered bare in unit tests, which would
  // otherwise need an auth provider to render a row. An unresolved session
  // yields "", which disables the column's buttons with an explanatory title
  // rather than hiding them.
  const me = useMe();
  const meEmail = meEmailOverride ?? (me.status === "authenticated" ? me.data.email : "");

  const [owner, setOwner] = useState("me"); // default scope: mine
  // `?status=` seeds the INITIAL filter, so a caller arriving from a narrower
  // surface lands on the scope it promised — the dashboard widget counts
  // strictly-Open searches and its "View all N" would otherwise name a number
  // this page contradicts on every load where a BD search exists (PR #674
  // review I-3).
  //
  // Read once, on mount: after that the pills own the filter, and re-seeding
  // from the URL would fight a reader who has just clicked one. An unknown or
  // absent value falls back to this page's own default rather than emptying
  // the list. Two consequences worth knowing: a pill click writes the choice
  // BACK to the URL (below) so a reload or shared link shows what was on
  // screen; and because the initializer runs only on mount, a same-route
  // `<Link ...?status=bd>` rendered while already on this page would move the
  // URL without moving the list — nothing does that today (the widget's links
  // live on the dashboard, so arrival always mounts).
  const [status, setStatus] = useState<SearchStatusFilter>(
    () => readStatusParam(searchParams) ?? "open_bd"
  );
  // `?sort=&order=` seed the sort the same way `?status=` seeds the filter:
  // once, on mount, and written back on every header click (below) so a
  // reload or a shared link shows the order that was on screen — the epic's
  // acceptance criterion "the sort survives a reload". Unknown → no sort.
  const [sort, setSort] = useState<SearchesSort>(() => readSortParams(searchParams));
  const [searchText, setSearchText] = useState(""); // raw input
  const [q, setQ] = useState(""); // debounced → wire
  const [page, setPage] = useState(1);

  // Debounce the search input into `q`; reset to page 1 on a new search.
  // Only when the box differs from what is applied: without that guard the
  // timer also ran on mount and, 300 ms in, sent a reader who had already
  // clicked Next back to page 1 under an unchanged box.
  useEffect(() => {
    if (searchText === q) return;
    const id = setTimeout(() => {
      setQ(searchText);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchText, q]);

  const query = useSearchesList({
    client: activeClient,
    owner,
    status,
    q,
    sort,
    page,
    pageSize,
  });

  // The owner dropdown's colleague options. Its own query, keyed on `status`
  // alone — see the hook. Failure is silent BY DESIGN: the list is the page,
  // and losing the colleague options leaves `Mine` / `Everyone` working, so an
  // error banner here would interrupt a reader over a control they may never
  // touch.
  const leadsQuery = useSearchLeads({ client: activeClient, status });
  const leads = leadsQuery.data?.items ?? [];

  // The SCOPE — whose searches these are, in what order. Owner, status and the
  // sort, deliberately NOT `q` and not the page.
  //
  // These are the claims a stale row set falsifies: rendering the caller's own
  // rows under a pressed Everyone pill, Open rows under the BD pill, or rows in
  // the previous order under a header carrying `aria-sort` and a `?sort=` in
  // the URL (PR #973 review iter 1, C-10), is a lie about what is on screen,
  // so a change to any of them must blank until the server answers. `q`
  // narrows the SAME scope and is set 300 ms after the last keystroke, so
  // including it here made every debounced keystroke batch a scope change —
  // the table and its pagination were replaced by a loading box on every tick
  // of the page's highest-frequency interaction, which is precisely the
  // flicker `placeholderData` exists to prevent. The claims a stale `q` would
  // make are suppressed where they are made instead: see `total` below.
  const scope = `${owner}|${status}|${sort?.key ?? ""}|${sort?.dir ?? ""}`;
  // The whole filter set. `total` is a claim about THESE, and unlike `scope` it
  // has to include `q`; unlike the query key it must not include the page,
  // because a page change does not change the total.
  const filters = `${scope}|${q}`;

  // The last response the server actually confirmed, and the scope it described.
  // `placeholderData` covers a page change only while it is PENDING — TanStack v5
  // drops back to `data: undefined` once that request ERRORS, since the new query
  // key never held data of its own. So without keeping the previous response
  // ourselves, one failed page request takes away the page the user was reading,
  // rows and pagination both, and a full reload is the only way back.
  //
  // Scoped, because an unscoped fallback substitutes the OTHER scope's answer: a
  // recruiter clicks Everyone, the request 500s, and the page shows the Everyone
  // pill pressed, their own 135 searches, and a banner claiming these are the
  // ones "we last loaded" — which is true of the rows and false of the toolbar
  // above them. Worse for the unresolved-identity case, where the notice telling
  // the user to switch to Everyone disappears the moment the switch fails, and
  // the table announces "no searches match the current filters" about a request
  // that errored.
  const lastLoaded = useRef<
    { scope: string; filters: string; response: SearchesListResponse } | undefined
  >(undefined);
  useEffect(() => {
    // `isPlaceholderData` guards the other direction: while a new scope is
    // pending, `query.data` is still the PREVIOUS scope's response, and recording
    // it under the new key would recreate exactly the substitution above.
    if (query.data && !query.isPlaceholderData) {
      lastLoaded.current = { scope, filters, response: query.data };
    }
  }, [query.data, query.isPlaceholderData, scope, filters]);

  const fallback = lastLoaded.current?.scope === scope ? lastLoaded.current.response : undefined;
  // The SAME guard the recorder above uses, on the render path. `placeholderData`
  // keeps `status: "success"` across a query-key change, so while a new scope is
  // in flight `query.data` is the previous scope's response and `isPending` is
  // false — the page rendered the Everyone pill pressed over the caller's own
  // rows, their own total and their own identity verdict, with no loading state,
  // for the whole duration of the request. The scoped `fallback` still covers the
  // same-scope page flicker `placeholderData` was bought for.
  const liveData = query.isPlaceholderData ? undefined : query.data;
  const shown = liveData ?? fallback;
  const items = shown?.items ?? [];
  // The pagination describes the ROWS on screen, so it follows `shown` — during a
  // text search those are the previous query's rows, and their own total is what
  // "Showing 1-20 of N" is about.
  const rowsTotal = shown?.total ?? 0;
  // The toolbar count is a different statement: a claim about the filters NOW in
  // the toolbar, so it may only come from a response to THOSE filters. `null`
  // renders as an em dash instead of a number. The previous `shown?.total ?? 0`
  // announced "My Searches (0)" for the whole duration of every scope change —
  // the same unanswered factual claim the blanking guard above removes from the
  // rows, surviving in the one place that guard did not look. A page change keeps
  // the count, because `total` does not depend on the page.
  const countedResponse =
    liveData ?? (lastLoaded.current?.filters === filters ? lastLoaded.current.response : undefined);
  const total = countedResponse?.total ?? null;
  // False only when the caller's email has no `nu_sync.users` match, so their
  // Thrive user id is unknown. Optimistic default: while the first request is
  // in flight there is nothing to warn about yet.
  const identityResolved = shown?.identity_resolved ?? true;

  function changeOwner(next: string) {
    setOwner(next);
    setPage(1);
  }

  function changeStatus(next: SearchStatusFilter) {
    setStatus(next);
    setPage(1);
    // Keep the URL honest: arriving on `?status=open` and clicking "Open + BD"
    // must not leave a bookmark/reload/share pointing at a scope the screen no
    // longer shows (PR #674 review iter 2, A§2b / C·N-2). `replace` because a
    // pill click is a filter tweak, not a navigation Back should undo — unlike
    // the detail page's `?tab=`, which is deliberately a push.
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set("status", next);
        return nextParams;
      },
      { replace: true }
    );
  }

  function changeSort(key: SearchesListSortKey) {
    // First click on a column: ascending. Same column again: flip. Another
    // column: ascending on that one — the toggle the client-side sort had, so
    // the header buttons behave exactly as before; only WHERE the ordering
    // happens changed. Back to page 1: the result set is re-ordered as a
    // whole, so "page 3 of the previous order" names nothing.
    const next: SearchesSort =
      sort?.key === key ? { key, dir: sort.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" };
    setSort(next);
    setPage(1);
    // Same `replace` reasoning as `changeStatus`: a header click is a view
    // tweak, not a navigation Back should undo.
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set("sort", next.key);
        nextParams.set("order", next.dir);
        return nextParams;
      },
      { replace: true }
    );
  }

  // "Mine" with an unresolvable Thrive identity is a provisioning problem, not
  // an empty result — the backend returns an empty page on purpose (it will
  // never fall back to the firm-wide list), so the copy has to say why and
  // point at the way out.
  const unresolvedIdentity = !identityResolved && owner === "me";
  const emptyMessage = unresolvedIdentity
    ? "We could not match your account to a Thrive user, so we cannot tell which searches are yours. Switch to Everyone to browse the firm's searches, and ask an administrator to link your Thrive account."
    : "No searches match the current filters.";

  return (
    <div data-testid="searches-page" className="flex flex-col gap-4 p-8">
      <SearchesToolbar
        total={total}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        owner={owner}
        onOwnerChange={changeOwner}
        leads={leads}
        status={status}
        onStatusChange={changeStatus}
      />

      {/* Only a FIRST load that failed gets the full-page error. Once a page has
          loaded, a later failure must not take the table and its pagination away
          from a reader who is looking at rows the server confirmed — same rule as
          SearchDetailPage, reached here through `lastLoaded` because TanStack
          drops the data on a NEW query key that errors. */}
      {query.isError && !shown ? (
        <div
          data-testid="searches-error"
          // A full-page failure that replaced what the reader was looking at:
          // `alert` interrupts, because the content they were reading is gone.
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
        >
          <span>Couldn’t load searches. Please try again.</span>
          <RetryButton
            onClick={() => void query.refetch()}
            busy={query.isFetching}
            busyLabel="Retrying…"
            tone="error"
          />
        </div>
      ) : (
        <>
          {query.isError ? (
            // A page request failed with rows already on screen. Say so and
            // offer the retry, but keep showing what the server last confirmed.
            // `<output>` rather than a div: it carries `role="status"` and a
            // polite live region natively, which is the form biome's
            // useSemanticElements wants (same reason the toolbar uses
            // `<fieldset>`). The non-destructive banners are the ones that most
            // need it — rows and figures stay exactly where they were and only
            // this text changes, so without a live region a screen-reader user
            // gets no signal that what they are reading is no longer what was
            // asked for. Polite, not `alert`, because nothing was taken away.
            //
            // The region wraps the MESSAGE only, with the button as a sibling:
            // `role="status"` is implicitly `aria-atomic="true"`, so a retry
            // button inside it makes its own `Retry… / Retrying…` label flip
            // re-announce the whole banner.
            <div
              data-testid="searches-stale-error"
              className="flex flex-wrap items-center gap-3 rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
            >
              <output>
                Couldn’t load that page just now. The searches below are the ones we last loaded.
              </output>
              <RetryButton
                onClick={() => void query.refetch()}
                busy={query.isFetching}
                busyLabel="Retrying…"
              />
            </div>
          ) : null}
          {unresolvedIdentity ? (
            <output
              data-testid="searches-identity-notice"
              className="rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning"
            >
              Your account isn’t linked to a Thrive user yet, so “Mine” has nothing to show.
            </output>
          ) : null}
          {!shown ? (
            // Nothing the server has confirmed FOR THIS SCOPE. "No searches match
            // the current filters" plus "Showing 0 searches" is a factual claim
            // about the filters now in the toolbar, so it may not be made before
            // a request under those filters has answered — on first load or on a
            // scope change. `!shown` rather than `query.isPending` for exactly
            // that second case: `isPending` is false throughout a scope change,
            // because the placeholder keeps the query in `success`. A same-scope
            // page change still has `fallback`, so it does not land here.
            <output
              data-testid="searches-loading"
              className="rounded-md border border-border bg-surface px-4 py-6 text-body text-text-secondary"
            >
              Loading searches…
            </output>
          ) : (
            <>
              {/* No `key={page}` any more: the sort lives here, not in the
                  table, so a page change has nothing to reset — the rows of
                  every page arrive in the one order the server applied. */}
              <SearchesTable
                items={items}
                emptyMessage={emptyMessage}
                meEmail={meEmail}
                sort={sort}
                onSort={changeSort}
              />
              {/* `page`, the REQUESTED page — not the page the rows came from.
                  The control computes its targets from this prop, so pointing it
                  at the loaded page after a failed jump to page 2 made "Next"
                  call `setPage(2)` with 2 already in state: React bails on the
                  unchanged value, no key change, no request, and the two
                  controls a reader reaches for first go silently dead. It also
                  keeps `aria-current` on the page the app is actually asking
                  for, which is what the banner above is talking about. */}
              <TablePagination
                total={rowsTotal}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                noun="search"
                nounPlural="searches"
                testId="searches-pagination"
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
